import { Typography, TypographyProps } from "@sunday/component";
import React from "react";
import styled from "styled-components";

export type LegacyTypographyProps = Omit<
  TypographyProps,
  "designLevel" | "weight"
> & {
  level?: number;
  weight?: "regular" | "bold";
};

/**
 * Temporary adapter for the application-form components that still pass the
 * numeric typography levels from Sunday Component V2. The rendered text is
 * always a Lunar Typography primitive from Sunday Component V3.
 */
export const LegacyTypography = ({
  level = 7,
  weight,
  ...props
}: LegacyTypographyProps) => {
  if (level <= 3) {
    return (
      <Typography.Headline
        designLevel={2}
        weight={weight || "bold"}
        {...props}
      />
    );
  }
  if (level === 4) {
    return (
      <Typography.Headline
        designLevel={1}
        weight={weight || "bold"}
        {...props}
      />
    );
  }
  if (level <= 6) {
    return (
      <Typography.Headline
        designLevel={3}
        weight={weight || "bold"}
        {...props}
      />
    );
  }
  if (level === 7) {
    return (
      <Typography.Body
        designLevel={2}
        weight={weight || "regular"}
        {...props}
      />
    );
  }
  return (
    <Typography.Label
      designLevel={level === 8 ? 1 : 2}
      weight={weight || "regular"}
      {...props}
    />
  );
};

export const PageTitle = styled(Typography.Display).attrs({
  designLevel: 3,
  weight: "bold",
})`
  && {
    color: var(--color-text-primary, #202123);
    margin: 0;
  }
`;

export const SectionTitle = styled(Typography.Headline).attrs({
  designLevel: 2,
  weight: "bold",
})`
  && {
    color: var(--color-text-primary, #202123);
    margin: 0;
  }
`;

export const CardTitle = styled(Typography.Headline).attrs({
  designLevel: 3,
  weight: "bold",
})`
  && {
    color: var(--color-text-primary, #202123);
    margin: 0;
  }
`;

export const ModalTitle = styled(Typography.Title).attrs({
  designLevel: 2,
  weight: "bold",
})`
  && {
    --typography-base-font-family: var(
      --typescale-font-base,
      var(--typescale-font-th, "Noto Sans Thai"),
      sans-serif
    );
    --typography-base-font-weight-bold: var(--typescale-weight-base-bold, 600);
    --typography-t2-font-size: var(--typescale-size-t-2, 20px);
    --typography-t2-line-height: var(--typescale-line-height-t-2, 32px);

    display: block;
    width: 100%;
    max-width: 255px;
    margin: 0;
    color: var(--color-text-primary, #202123);
    font-style: normal;
    letter-spacing: var(--typescale-tracking-base, 0);
    text-align: center;
    word-break: break-word;
  }
`;

export const ModalBodyText = styled(Typography.Body).attrs({
  designLevel: 2,
  weight: "regular",
})`
  && {
    --typography-base-font-family: var(
      --typescale-font-base,
      var(--typescale-font-th, "Noto Sans Thai"),
      sans-serif
    );
    --typography-base-font-weight-regular: var(--typescale-weight-400, 400);
    --typography-b2-font-size: var(--typescale-size-b-2, 15px);
    --typography-b2-line-height: var(--typescale-line-height-b-2, 24px);
    --typography-color: #5f6468;

    display: block;
    width: 100%;
    max-width: 255px;
    margin: 0;
    color: #5f6468;
    font-style: normal;
    letter-spacing: var(--typescale-tracking-base, 0);
    text-align: center;
    vertical-align: middle;
    white-space: pre-line;
    word-break: break-word;

    * {
      color: inherit;
      text-align: center;
    }
  }
`;

export const BodyText = styled(Typography.Body).attrs({
  designLevel: 2,
})`
  && {
    color: var(--color-text-primary, #202123);
    margin: 0;
  }
`;

export const BodyTextBold = styled(Typography.Body).attrs({
  designLevel: 2,
  weight: "bold",
})`
  && {
    color: var(--color-text-primary, #202123);
    margin: 0;
  }
`;

type FieldTypographyProps = Omit<TypographyProps, "designLevel" | "weight"> & {
  level: number;
};

const fieldTypographyStyle = {
  fontFamily: 'var(--typescale-font-th, "Noto Sans Thai"), sans-serif',
  letterSpacing: "var(--typescale-tracking-base, 0)",
  margin: 0,
};

export const FieldLabelText = ({
  level: _level,
  className,
  style,
  ...props
}: FieldTypographyProps) => (
  <Typography.Body
    {...props}
    className={["icth-field-label", className].filter(Boolean).join(" ")}
    designLevel={2}
    weight="bold"
    style={{ ...fieldTypographyStyle, ...style }}
  />
);

export const FieldOptionalText = ({
  level: _level,
  className,
  style,
  ...props
}: FieldTypographyProps) => (
  <Typography.Body
    {...props}
    className={["icth-field-optional", className].filter(Boolean).join(" ")}
    designLevel={2}
    weight="regular"
    style={{ ...fieldTypographyStyle, fontWeight: 400, ...style }}
  />
);

export const LabelText = styled(Typography.Label).attrs({
  designLevel: 1,
})`
  && {
    color: var(--color-text-primary, #202123);
    margin: 0;
  }
`;

export const LabelTextBold = styled(Typography.Label).attrs({
  designLevel: 1,
  weight: "bold",
})`
  && {
    color: var(--color-text-primary, #202123);
    margin: 0;
  }
`;

export const ActionText = styled(Typography.Body).attrs({
  designLevel: 2,
  weight: "bold",
})`
  && {
    color: var(--color-text-ci-pri, #435dff);
    margin: 0;
  }
`;
